/**
 * 模組頁面代碼 : 1330-UI-05
 * 頁面說明 : 住戶新增修改視窗
 * @module web/modules/resident/components/ResidentModal
 */

// ----- import from npm modules -----
import React, { useState, useEffect } from 'react';

// ----- constants -----
const RESIDENT_NEW = 'new';
const RESIDENT_EDIT = 'edit';

const RESPONSE_OK = 200;
const TITLE_RESIDENT_NEW = '新增住戶資料';
const TITLE_RESIDENT_EDIT = '修改住戶資料';

const ResidentModal = React.forwardRef((props, ref) => {
    const { mode, resident, buildingSelection, searchResident } = props;
    let modalIcon = mode === RESIDENT_NEW? '#ico-plus' : '#ico-edit';
    let modalTitleText = mode === RESIDENT_NEW? TITLE_RESIDENT_NEW : TITLE_RESIDENT_EDIT;

    // 初始化state
    const [formValues, setFormValues] = useState({
        modalResidentName: '',
        modalResidentMobile: '',
        modalResidentBuilding: '',
        modalResidentHouseNum: '',
        modalResidentFloorDisplay: '',
        modalResidentRoomNum: '',
        modalResidentMemo: '',
        modalResidentStatus: 'A',
        modalApplyBuilding: '',
        modalApplyElevator: '',
        modalApplyFloor: '',
    });

    const[elevatorList, setElevatorList] = useState([]);
    const[floorList, setFloorList] = useState([]);
    const [invalidShow, setInvalidShow] = useState('none');

    // 更新電梯選項
    const updateElevatorList = (buildingId) => {
        const building = buildingSelection.find(building => building.buildingId === buildingId);
        setElevatorList(building?.elevators || []);
    };

    // 更新樓層選項
    const updateFloorList = (buildingId, elevatorId) => {
        const building = buildingSelection.find(building => building.buildingId === buildingId);
        const elevator = building?.elevators.find(elevator => elevator.elevatorId === elevatorId);
        setFloorList(elevator?.floors || []);
    };


    // Handle change event
    const handleChange = (event) => {
        const target = event.target;
        let value = target.value;

        if (target.type === 'radio') {
            value = target.id.slice(-1);
        }

        if (target.name === 'modalApplyBuilding') {
            const buildingId = parseInt(value);
            updateElevatorList(buildingId);
            const elevatorId = parseInt(formValues.modalApplyElevator);
            updateFloorList(buildingId, elevatorId);
        }

        if (target.name === 'modalApplyElevator') {
            const buildingId = parseInt(formValues.modalApplyBuilding);
            const elevatorId = parseInt(value);
            updateFloorList(buildingId, elevatorId);
        }

        setFormValues({
            ...formValues,
            [target.name]: value,
        });

    };


    // 使用useEffect在resident改變才更新formValues
    useEffect(() => {
        setFormValues({
            modalResidentName: resident? resident.applicantName : '',
            modalResidentMobile: resident? resident.mobile : '',
            modalResidentBuilding: resident? resident.resident.buildingId : '',
            modalResidentHouseNum: resident? resident.resident.houseNum : '',
            modalResidentFloorDisplay: resident? resident.resident.floorDisplay : '',
            modalResidentRoomNum: resident? resident.resident.roomNum : '',
            modalResidentMemo: resident? resident.resident.memo : '',
            modalResidentStatus: resident? resident.flgStatus : 'A',
            modalApplyBuilding: resident? resident.resident.buildingId : '',
            modalApplyElevator: '',
            modalApplyFloor: '',
        });

        if (resident) {
            updateElevatorList(resident.resident.buildingId);
        } else {
            setElevatorList([]);
            setFloorList([]);
        }

        setInvalidShow('none');
    }, [resident]);

    const handleSubmit = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const form = e.currentTarget;

        if (form.checkValidity() === false) {
            setInvalidShow('block');
            form.classList.add('was-validated');
            return;
        }

        // 建立 FormData 讀取表单的值
        const formData = new FormData(form);

        // 建立住戶物件
        const saveResident = {
            residentId: mode === RESIDENT_NEW? null : resident.residentId,
            applyBuildingId: parseInt(formData.get('modalApplyBuilding')),
            applyElevatorId: parseInt(formData.get('modalApplyElevator')),
            applyFloorNum: parseInt(formData.get('modalApplyFloor')),
            name: formData.get('modalResidentName'),
            mobile: formData.get('modalResidentMobile'),
            buildingId: parseInt(formData.get('modalResidentBuilding')),
            houseNum: formData.get('modalResidentHouseNum'),
            floorDisplay: formData.get('modalResidentFloorDisplay'),
            roomNum: formData.get('modalResidentRoomNum'),
            memo: formData.get('modalResidentMemo'),
            flgStatus: formData.get('modalResidentStatus'),
        };

        // 呼叫父層元件的新增或修改角色函式
        if (mode === RESIDENT_NEW) {
            props.addResident(saveResident).then((response) => {
                const statusCode = response.payload.code;
                if (statusCode !== RESPONSE_OK) {
                    $(ref.current).modal('hide');
                    props.onErrorHandle(TITLE_RESIDENT_NEW, response.payload);
                } else {
                    // 调用 fetchResidentList and Hide the modal
                    const { buildingId, houseNum, elevatorId, floorDisplay } = searchResident;
                    props.fetchResidentList(buildingId, houseNum, elevatorId, floorDisplay).then(() => {
                        props.setNeedResidentListLoad(true);
                        $(ref.current).modal('hide');
                    });
                }
            });
        } else if (mode === RESIDENT_EDIT) {
            props.editResident(saveResident).then((response) => {
                const statusCode = response.payload.code;
                if (statusCode !== RESPONSE_OK) {
                    $(ref.current).modal('hide');
                    props.onErrorHandle(TITLE_RESIDENT_EDIT, response.payload);
                } else {
                    // 调用 fetchResidentList and Hide the modal
                    const { buildingId, houseNum, elevatorId, floorDisplay } = searchResident;
                    props.fetchResidentList(buildingId, houseNum, elevatorId, floorDisplay).then(() => {
                        props.setNeedResidentListLoad(true);
                        $(ref.current).modal('hide');
                    });
                }
            });
        }
    }

    return (
        <div ref={ref}
            className="modal fade"
            id="modalResident"
            data-bs-backdrop="static"
            data-bs-keyboard="false"
            tabIndex="-1"
            aria-labelledby="modalResidentLabel"
            aria-hidden="true"
        >
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                <form className="modal__form modal-content needs-validation" id="modalResidentForm" onSubmit={handleSubmit}>
                    <div className="modal-header">
                        <h1 className="modal-title fs-5" id="modalResidentLabel">
                            <div className="modal__title__badge">
                                <svg className="badge__icon" aria-hidden="true">
                                    <use href={modalIcon}></use>
                                </svg>
                            </div>
                            <div className="modal__title__text">{modalTitleText}</div>
                        </h1>
                        <button type="button" className="btn-close btn-cancel" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <div className="d-flex flex-column gap-3 px-2">
                            <div className="modal__form__catagory row">
                                <div className="col-3">
                                    <div className="modal__form__catagory__lable">住戶資料</div>
                                </div>
                                <div className="col-9">
                                    <div className="row">
                                        <div className="col-6">
                                            <label htmlFor="modalResidentName" className="form-label">住戶姓名*</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                id="modalResidentName"
                                                name="modalResidentName"
                                                value={formValues.modalResidentName}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                        <div className="col-6">
                                            <label htmlFor="modalResidentMobile" className="form-label">手機號碼*</label>
                                            <input
                                                type="tel"
                                                pattern="09[0-9]{8}"
                                                className="form-control form-control-sm"
                                                id="modalResidentMobile"
                                                name="modalResidentMobile"
                                                value={formValues.modalResidentMobile}
                                                onChange={handleChange}
                                                required
                                            />
                                            <div className="invalid-feedback">請輸入正確格式</div>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-6">
                                            <label htmlFor="modalResidentHouseNum" className="form-label">戶號*</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                id="modalResidentHouseNum"
                                                name="modalResidentHouseNum"
                                                value={formValues.modalResidentHouseNum}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                        <div className="col-6">
                                            <label htmlFor="modalResidentBuilding" className="form-label">大樓名稱*</label>
                                            <select
                                                className="form-select form-select-sm"
                                                id="modalResidentBuilding"
                                                name="modalResidentBuilding"
                                                value={formValues.modalResidentBuilding}
                                                onChange={handleChange}
                                                required>
                                                    <option disabled value="">請選擇</option>
                                                    {buildingSelection.map((building, index) => (
                                                        <option key={index} value={building.buildingId}>{building.buildingName}</option>
                                                    ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-6">
                                            <label htmlFor="modalResidentFloorDisplay" className="form-label">樓層*</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                id="modalResidentFloorDisplay"
                                                name="modalResidentFloorDisplay"
                                                value={formValues.modalResidentFloorDisplay}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                        <div className="col-6">
                                            <label htmlFor="modalResidentRoomNum" className="form-label">室號</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                id="modalResidentRoomNum"
                                                name="modalResidentRoomNum"
                                                value={formValues.modalResidentRoomNum}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-12">
                                            <label htmlFor="modalResidentMemo" className="form-label">備註</label>
                                            <textarea
                                                className="form-control"
                                                id="modalResidentMemo"
                                                name="modalResidentMemo"
                                                value={formValues.modalResidentMemo}
                                                onChange={handleChange}
                                                rows="2"
                                            ></textarea>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {mode === RESIDENT_NEW && (
                            <div className="modal__form__catagory row">
                                <div className="col-3">
                                    <div className="modal__form__catagory__lable">梯控大樓/電梯/樓層</div>
                                </div>
                                <div className="col-9">
                                    <div className="row">
                                        <div className="col-12">
                                            <label htmlFor="modalApplyBuilding" className="form-label">大樓名稱*</label>
                                            <select
                                                className="form-select form-select-sm"
                                                id="modalApplyBuilding"
                                                name="modalApplyBuilding"
                                                value={formValues.modalApplyBuilding}
                                                onChange={handleChange}
                                                required>
                                                    <option disabled value="">請選擇</option>
                                                    {buildingSelection.map((building, index) => (
                                                        <option key={index} value={building.buildingId}>{building.buildingName}</option>
                                                    ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-6">
                                            <label htmlFor="modalApplyElevator" className="form-label">電梯編號*</label>
                                            <select
                                                className="form-select form-select-sm"
                                                id="modalApplyElevator"
                                                name="modalApplyElevator"
                                                value={formValues.modalApplyElevator}
                                                onChange={handleChange}
                                                required>
                                                <option disabled value="">請選擇</option>
                                                {elevatorList && elevatorList.map((elevator, index) => (
                                                    <option key={index} value={elevator.elevatorId}>{elevator.elevatorName}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-6">
                                            <label htmlFor="modalApplyFloor" className="form-label">樓層*</label>
                                            <select
                                                className="form-select form-select-sm"
                                                id="modalApplyFloor"
                                                name="modalApplyFloor"
                                                value={formValues.modalApplyFloor}
                                                onChange={handleChange}
                                                required
                                            >
                                                <option disabled value="">請選擇</option>
                                                {floorList && floorList.map((floor, index) => (
                                                    <option key={index} value={floor.floorNum}>{floor.floorDisplay}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            )}
                            <div className="modal__form__catagory row">
                                <div className="col-3">
                                    <div className="modal__form__catagory__lable">狀態*</div>
                                </div>
                                <div className="col-9">
                                    <div className="row">
                                        <div className="col-12">
                                            <div className="btn-group btn-group-sm" role="group" aria-label="radio toggle button group">
                                                <input
                                                    type="radio"
                                                    className="btn-check"
                                                    name="modalResidentStatus"
                                                    id="modalResidentStatusA"
                                                    autoComplete="off"
                                                    value={formValues.modalResidentStatus}
                                                    checked={formValues.modalResidentStatus === 'A'}
                                                    onChange={handleChange}
                                                    data-checkbox-group="required"
                                                />
                                                <label className="btn btn-outline-secondary" htmlFor="modalResidentStatusA">有效</label>

                                                <input
                                                    type="radio"
                                                    className="btn-check"
                                                    name="modalResidentStatus"
                                                    id="modalResidentStatusI"
                                                    autoComplete="off"
                                                    value={formValues.modalResidentStatus}
                                                    checked={formValues.modalResidentStatus === 'I'}
                                                    onChange={handleChange}
                                                    data-checkbox-group="required"
                                                />
                                                <label className="btn btn-outline-secondary" htmlFor="modalResidentStatusI">無效</label>

                                                <input
                                                    type="radio"
                                                    className="btn-check"
                                                    name="modalResidentStatus"
                                                    id="modalResidentStatusB"
                                                    autoComplete="off"
                                                    value={formValues.modalResidentStatus}
                                                    checked={formValues.modalResidentStatus === 'B'}
                                                    onChange={handleChange}
                                                    data-checkbox-group="required"
                                                />
                                                <label className="btn btn-outline-secondary" htmlFor="modalResidentStatusB">黑名單</label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12">
                                    <div className="alert alert-warning global-invalid-feedback" style={{ display: invalidShow }}></div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-cancel" data-bs-dismiss="modal">取消</button>
                        <button type="submit" className="btn btn-primary">儲存</button>
                    </div>
                </form>
            </div>
        </div>
    );
});

export default ResidentModal;